import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import elementSettings from 'src/js/modules/element-settings';
import controlIds from '../constants/control-ids';
import previewGifs from '../constants/preview-gifs';
import getBatchToResetCustomColWidth from '../helpers/get-batch-to-reset-custom-col-width';

const {
  Context: ElementSettingsContext,
  components: { SelectControl },
  constants: { breakpointKeys },
  helpers: { createBatch, combineBatches, convertPlainBatchToBreakpointBasedBatch },
} = elementSettings;

const FIXED_VALUE = '0';
const FILL_VALUE = '1';

const computeFallbackValue = () => FIXED_VALUE;

const CHOICES = [
  { name: () => i18next.t('Fixed'), value: FIXED_VALUE },
  { name: () => i18next.t('Fill'), value: FILL_VALUE },
];

function WidthControl({ elementIds, targets, nested = false, onChange }) {
  const { deviceMode } = useContext(ElementSettingsContext);
  const isBreakpointEditingEnabled = Boolean(deviceMode?.active);

  const handleChange = useCallback(
    ({ controlId, elementIds: ids, targets: growTargets, value }) => {
      const breakpointKey = isBreakpointEditingEnabled
        ? deviceMode.breakpointKey
        : breakpointKeys.DEFAULT;
      const growBatch = createBatch({
        elementIds: ids,
        targets: growTargets,
        value: { [breakpointKey]: value },
      });

      if (value !== FILL_VALUE) {
        onChange({
          controlId,
          elementIds: ids,
          targets: growTargets,
          value,
          batch: growBatch,
        });
        return;
      }

      const breakpointKeysToReset = isBreakpointEditingEnabled
        ? [breakpointKey]
        : [breakpointKeys.DEFAULT, breakpointKeys.MAX_XL, breakpointKeys.MAX_MD];
      const resetBatches = breakpointKeysToReset.map((key) =>
        convertPlainBatchToBreakpointBasedBatch(getBatchToResetCustomColWidth(ids, key), key)
      );

      onChange({
        controlId,
        elementIds: ids,
        targets: growTargets,
        value,
        batch: combineBatches([growBatch, ...resetBatches]),
      });
    },
    [deviceMode, isBreakpointEditingEnabled, onChange]
  );

  return (
    <SelectControl
      id={controlIds.COLUMN_WIDTH}
      elementIds={elementIds}
      targets={targets}
      title={i18next.t('Column width')}
      choices={CHOICES.map(({ name, value }) => ({ name: name(), value }))}
      mixedLabel={i18next.t('Mixed')}
      computeFallbackValue={computeFallbackValue}
      nested={nested}
      popover={{
        content: i18next.t('Stretch columns to the full width of the container'),
        imgSrc: previewGifs.COLUMNS_WIDTH_GIF,
      }}
      onChange={handleChange}
    />
  );
}

WidthControl.propTypes = {
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  targets: PropTypes.arrayOf(PropTypes.string).isRequired,
  nested: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default WidthControl;
